import type { FC, ReactNode } from 'react'

import {
    ChevronDown,
    Heart,
    Sparkles
} from 'lucide-react'
import { cn } from '@/lib/utils'
import Countdown from '@/components/landing/countdown'
import MemoryPhoto from '@/components/landing/memory-photo'
import FloatingHearts from '@/components/landing/floating-hearts'
import { HERO, PHOTOS } from '@/constants/birthday'

interface HeroSectionProps {
    revealed: boolean
}

const HeroSection: FC<HeroSectionProps> = ({ revealed }): ReactNode => {
    const enter = revealed
        ? 'animate-in fade-in slide-in-from-bottom-6 duration-1000 fill-mode-both motion-reduce:animate-none'
        : 'opacity-0'

    return (
        <section id='hero' aria-labelledby='hero-title' className='relative isolate overflow-hidden'>
            <div aria-hidden className='pointer-events-none absolute inset-0 -z-10 overflow-hidden'>
                <div className='absolute -left-32 -top-32 size-[28rem] rounded-full bg-accent blur-3xl' />
                <div className='absolute -right-24 top-1/3 size-96 rounded-full bg-gold/20 blur-3xl' />
                <div className='absolute -bottom-40 left-1/4 size-96 rounded-full bg-primary/10 blur-3xl' />
            </div>

            <FloatingHearts className='-z-10 text-primary' />

            <div className='mx-auto grid min-h-[100svh] max-w-6xl items-center gap-14 px-5 pb-28 pt-16 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:py-24'>
                <div className='text-center lg:text-left'>
                    <p className={cn('inline-flex items-center gap-2 rounded-full border border-primary/20 bg-white/70 px-4 py-1.5 text-sm font-bold text-primary shadow-sm backdrop-blur', enter)}>
                        <Sparkles className='size-4 shrink-0' />
                        {HERO.eyebrow}
                    </p>

                    <h1 id='hero-title' className={cn('mt-6', enter)} style={{ animationDelay: '120ms' }}>
                        <span className='block font-heading text-5xl font-semibold tracking-tight sm:text-6xl lg:text-7xl'>
                            {HERO.greeting}
                        </span>

                        <span className='block font-script text-8xl leading-[1.15] text-primary sm:text-9xl'>
                            {HERO.name}
                        </span>
                    </h1>

                    <p
                        className={cn('mx-auto mt-5 max-w-xl text-lg leading-relaxed text-foreground/80 text-pretty sm:text-xl lg:mx-0', enter)}
                        style={{ animationDelay: '240ms' }}
                    >
                        {HERO.subtitle}
                    </p>

                    <div className={enter} style={{ animationDelay: '360ms' }}>
                        <Countdown className='mt-10' />
                    </div>
                </div>

                <div
                    className={cn(
                        'relative mx-auto w-full max-w-[18rem] sm:max-w-sm lg:max-w-md',
                        revealed ? 'animate-in fade-in zoom-in-95 duration-1000 fill-mode-both motion-reduce:animate-none' : 'opacity-0'
                    )}
                    style={{ animationDelay: '200ms' }}
                >
                    <div aria-hidden className='absolute -inset-4 rounded-t-full rounded-b-[2.75rem] border-2 border-dashed border-primary/30' />

                    <div className='relative rounded-t-full rounded-b-[2.25rem] bg-white p-2.5 shadow-2xl shadow-primary/20'>
                        <MemoryPhoto
                            photo={PHOTOS.photo6}
                            loading='eager'
                            className='rounded-t-full rounded-b-[1.75rem]'
                        />
                    </div>

                    <p className='absolute -bottom-5 -left-3 -rotate-6 rounded-2xl bg-white px-5 py-1.5 font-script text-3xl text-primary shadow-lg sm:-left-6'>
                        {HERO.dateTag}
                    </p>

                    <Heart aria-hidden className='absolute -right-2 top-8 size-10 animate-heartbeat fill-primary text-primary motion-reduce:animate-none sm:-right-5' />
                    <Sparkles aria-hidden className='absolute -left-5 top-1/4 size-7 text-gold' />
                </div>
            </div>

            <a
                href='#story'
                className='absolute bottom-6 left-1/2 inline-flex -translate-x-1/2 flex-col items-center gap-1 rounded-full px-3 py-1 text-sm font-bold text-primary/80 transition hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
            >
                {HERO.scrollLabel}
                <ChevronDown className='size-5 animate-bounce motion-reduce:animate-none' />
            </a>
        </section>
    )
}

export default HeroSection
